import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import InfoMessage from '../../../components/info-messages/InfoMessage';
import VisibleIf from '../../../components/VisibleIf';

import PaymentMethodButton from '../payment-methods/PaymentMethodButton';
import Checkbox from '../../../components/input/Checkbox';
import Error from '../../../components/error/Error';
import { trackEvent, trackPixelEvent } from '../../../support-and-tracking/analytics-actions';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import { formatNumber } from '../../../utils';
import { getFormActions } from '../../../utils/form-helpers';
import formConfig from '../../../payment/invoice-form-config';
import RewardInfoForm from '../reward-info-form/RewardInfoForm';
import rewardInfoFormConfig, { getGiverAddress } from '../../../payment/reward-giver-info-form-config';
import EmailSuggestion from '../../../components/EmailSuggestion';
import styles from './invoice.module.less';
import RoundedButtonAction from '../../../components/button/rounded-button-action/RoundedButtonAction';
import Input2 from '../../../components/input/input-2/Input2';
import OrganizationNumberInput from '../../../components/input/OrganizationNumberInput';
import { doesAnyRewardNeedAddressOrPhonenumber, isAnyRewardLimited } from '../cart/cart-model';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../user/useUser';
import { useProject } from '../../project-hooks/useProject';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { getGreetingCardForPayment } from '../../../payment/payment-selectors';
import { createInvoice, fetchOrganizationFromExternalSource } from '../../../payment/invoice-actions';
import { Modal, ModalBlock } from '@sb1/ffe-modals-react';
import { Input, InputGroup } from '@sb1/ffe-form-react';
import RoundedButtonSecondary from '../../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import { Heading6 } from '@sb1/ffe-core-react';
import { ContextMessage } from '@sb1/ffe-messages-react';

const rewardFormActions = getFormActions(rewardInfoFormConfig);

const { setFormField, setFormFields, clearAll, validateForm, setErrors } = getFormActions(formConfig);

const Invoice = ({ amount, rewards, isCart = false, disabled = false, giverName, comment }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const project = useProject();
    const { subproject } = useSubproject();
    const [showRewardGiverInfoForm, setShowRewardGiverInfoForm] = useState(false);
    const eventId = project?.event_id;
    const showNameAndComment = project?.profile?.anonymousGiver;

    const tvaEvent = useSelector((state) => state.campaigns.tvaEventId);
    const p3aEvent = useSelector((state) => state.campaigns.p3aEventId);
    const od2022EventId = useSelector((state) => state.campaigns.od2022EventId);

    const user = useUser();
    const invoiceForm = useSelector((state) => state.invoiceForm);
    const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);
    const greetingCard = useSelector((state) => getGreetingCardForPayment(state, project?.id));

    const isTva = eventId && [p3aEvent, tvaEvent].includes(eventId.toString());
    const isOd = eventId && eventId === parseInt(od2022EventId, 10);
    const { isCreatingInvoice, errors = {} } = invoiceForm;
    const modalRef = useRef();

    useEffect(() => {
        return () => {
            dispatch(clearAll());
        };
    }, [dispatch]);

    useEffect(() => {
        if (rewardGiverInfoForm.hasEnteredAddress) {
            if (showRewardGiverInfoForm) {
                setShowRewardGiverInfoForm(false);
                modalRef.current.open();
            }
        }
    }, [rewardGiverInfoForm.hasEnteredAddress]);

    const buttonClicked = () => {
        trackPixelEvent('InitiateCheckout');
        trackEvent('support', 'checkout-provider', {
            method: 'Invoice',
            'project-id': project.id,
            'subproject-id': subproject?.id,
            cart: isCart,
            reward: rewards?.length > 0,
            amount,
        });

        dispatch(setFormFields({ amount, projectId: project.id, rewards, subprojectId: subproject?.id }));

        if (user.id) {
            dispatch(setFormFields({ email: user.email }));
        }
        dispatch(setFormField('giverName', giverName));
        dispatch(setFormField('comment', comment));

        if (doesAnyRewardNeedAddressOrPhonenumber({ rewards }) && !rewardGiverInfoForm.hasEnteredAddress) {
            setShowRewardGiverInfoForm(true);
        } else {
            modalRef.current.open();
        }
    };

    const startCreateInvoice = (e) => {
        e.preventDefault();
        const errors = dispatch(validateForm());
        if (isEmpty(errors)) {
            const rewardGiverInfoMetadata = doesAnyRewardNeedAddressOrPhonenumber({ rewards })
                ? getGiverAddress(rewardGiverInfoForm)
                : null;
            dispatch(setFormField('isCreatingInvoice', true));
            trackEvent('support', 'checkout-pay', {
                method: 'Invoice',
                'project-id': project.id,
                'subproject-id': subproject?.id,
                amount: invoiceForm.amount,
                cart: isCart,
                reward: rewards?.length > 0,
                giverName: !!giverName,
                comment: !!comment,
            });
            dispatch(createInvoice(rewardGiverInfoMetadata, greetingCard))
                .then((invoice) => {
                    trackPixelEvent('Purchase', {
                        value: invoiceForm.amount,
                        currency: 'NOK',
                    });
                    trackEvent('support', 'checkout-complete', {
                        method: 'Invoice',
                        'project-id': project.id,
                        'subproject-id': subproject?.id,
                        amount: invoiceForm.amount,
                        cart: isCart,
                        reward: rewards?.length > 0,
                        giverName: !!giverName,
                        comment: !!comment,
                    });
                    navigate(`/project/${project.id}/invoice-registered/${invoice.body.id}/${invoice.body.nonce}`);
                })
                .catch((error) => {
                    dispatch(setFormFields({ isCreatingInvoice: false }));
                    dispatch(setErrors(error.body?.errors));
                });
        }
    };

    if (!project) {
        return null;
    }

    return (
        <div>
            <PaymentMethodButton
                type="button"
                onClick={buttonClicked}
                disabled={isAnyRewardLimited({ rewards }) || disabled}
            >
                Faktura
            </PaymentMethodButton>
            <RewardInfoForm
                modalIsOpen={showRewardGiverInfoForm}
                closeModal={() => {
                    dispatch(rewardFormActions.clearAll());
                    setShowRewardGiverInfoForm(false);
                }}
                rewards={rewards}
            />
            <Modal ref={modalRef}>
                <ModalBlock>
                    <Heading6 textCenter>Faktura</Heading6>
                    <div className={styles.form}>
                        <form onSubmit={startCreateInvoice}>
                            {isCreatingInvoice ? (
                                <div className={styles.loader}>
                                    <LoaderBar subText={'Lager faktura…'} />
                                </div>
                            ) : (
                                <>
                                    <div>
                                        {errors.global ? (
                                            <div style={{ marginBottom: '1rem' }}>
                                                <Error error={errors.global} />
                                            </div>
                                        ) : null}
                                        <div>
                                            <Input2
                                                title="E-post"
                                                fieldName="email"
                                                value={invoiceForm.email}
                                                errors={errors}
                                                type="email"
                                                onChange={(e) =>
                                                    dispatch(setFormField('email', e.target.value.trim(), false))
                                                }
                                                onBlur={(e) =>
                                                    dispatch(setFormField('email', e.target.value.trim(), true))
                                                }
                                            />
                                            <EmailSuggestion
                                                className={styles.emailSuggestion}
                                                email={invoiceForm.email}
                                            />
                                        </div>
                                        <OrganizationNumberInput
                                            title="Organisasjonsnummer"
                                            fieldName="organizationNumber"
                                            value={invoiceForm.organizationNumber}
                                            error={errors.organizationNumber || errors.name}
                                            onChange={({ value }) => {
                                                const organizationNumber = value ? value.replace(/\s/g, '') : null;
                                                dispatch(
                                                    setFormFields(
                                                        {
                                                            organizationNumber,
                                                            name: null,
                                                            address1: null,
                                                            postalPlace: null,
                                                            postalCode: null,
                                                        },
                                                        false
                                                    )
                                                );
                                                dispatch(fetchOrganizationFromExternalSource(organizationNumber));
                                            }}
                                            onBlur={() =>
                                                dispatch(
                                                    setFormField(
                                                        'organizationNumber',
                                                        invoiceForm.organizationNumber,
                                                        true
                                                    )
                                                )
                                            }
                                        />
                                        {invoiceForm.name && (
                                            <div className={styles.organizationAddressInfo}>
                                                <div>{invoiceForm.name}</div>
                                                <div>{invoiceForm.address1}</div>
                                                <div>{invoiceForm.postalCode + ' ' + invoiceForm.postalPlace}</div>
                                            </div>
                                        )}
                                        {isTva && (
                                            <p className={styles.invoiceShareDataInfo}>
                                                E-postadresse, beløp og organisasjonsnummer deles med TV-aksjonen.
                                            </p>
                                        )}
                                        <div>
                                            <Input2
                                                title="Kontaktperson i bedriften (valgfritt)"
                                                fieldName="contactPersonAtCompany"
                                                value={invoiceForm.contactPersonAtCompany}
                                                errors={errors}
                                                type="text"
                                                maxLength={100}
                                                onChange={(e) =>
                                                    dispatch(
                                                        setFormField('contactPersonAtCompany', e.target.value, false)
                                                    )
                                                }
                                                onBlur={(e) =>
                                                    dispatch(
                                                        setFormField('contactPersonAtCompany', e.target.value, true)
                                                    )
                                                }
                                            />
                                            {isOd && (
                                                <p className={styles.fieldHelpText}>
                                                    Dette er kontaktpersonen i bedriften eleven har jobbet for.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <InputGroup
                                                label={'Kommentar (valgfritt)'}
                                                description={
                                                    'Dette kan f.eks. være ordrenummer, ressursnummer, PO-nummer, etc.'
                                                }
                                                fieldMessage={errors?.['resourceNumber']}
                                            >
                                                <Input
                                                    name={'resourceNumber'}
                                                    value={invoiceForm.resourceNumber}
                                                    maxLength={100}
                                                    onChange={(e) =>
                                                        dispatch(setFormField('resourceNumber', e.target.value, false))
                                                    }
                                                    onBlur={(e) =>
                                                        dispatch(setFormField('resourceNumber', e.target.value, true))
                                                    }
                                                />
                                            </InputGroup>
                                        </div>
                                        <div className={styles.sendAsEhfOption}>
                                            <Checkbox
                                                title="Motta som EHF-faktura"
                                                fieldName="sendAsEhf"
                                                checked={invoiceForm.sendAsEhf}
                                                onChange={(e) => dispatch(setFormField('sendAsEhf', e.target.checked))}
                                            />
                                        </div>
                                        {!!project?.profile?.invoiceTaxDeduction && (
                                            <>
                                                <div className={styles.taxDeductionOption}>
                                                    <Checkbox
                                                        title={`Del organisasjonsnummer med ${
                                                            project?.receiver?.name || 'mottaker'
                                                        } for mulighet til å få skattefradrag`}
                                                        fieldName="storeGiverData"
                                                        checked={invoiceForm.taxDeduction}
                                                        onChange={(e) =>
                                                            dispatch(setFormField('taxDeduction', e.target.checked))
                                                        }
                                                    />
                                                </div>
                                                {invoiceForm.taxDeduction && (
                                                    <ContextMessage
                                                        type="info"
                                                        compact
                                                        className={styles.taxDeductionInfo}
                                                    >
                                                        Det er {project?.receiver?.name || 'mottaker'} som rapporterer
                                                        gaven til skattemyndighetene.
                                                    </ContextMessage>
                                                )}
                                            </>
                                        )}
                                        <VisibleIf isVisible={showNameAndComment}>
                                            <div className={styles.giverListOption}>
                                                <InfoMessage className={styles.giverListInformation}>
                                                    Ditt bidrag blir synlig på spleisen først etter at du har betalt
                                                    fakturaen. Hvis du ønsker synlighet på spleisen umiddelbart kan du
                                                    betale med kort eller Vipps.
                                                </InfoMessage>
                                            </div>
                                        </VisibleIf>
                                    </div>

                                    <div className={styles.buttons}>
                                        <RoundedButtonSecondary
                                            onClick={() => {
                                                modalRef.current.close();
                                            }}
                                        >
                                            Avbryt
                                        </RoundedButtonSecondary>
                                        <RoundedButtonAction onClick={startCreateInvoice}>
                                            Send faktura på {formatNumber(amount)} kr
                                        </RoundedButtonAction>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </ModalBlock>
            </Modal>
        </div>
    );
};

Invoice.propTypes = {
    amount: PropTypes.number,
    rewards: PropTypes.array,
    isCart: PropTypes.bool,
};

export default Invoice;
